import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationsSummaryDto, PortalModule } from '@ciimar/shared';
import { api } from '../api/client';
import { useI18n } from '../lib/i18n';
import { Icon } from './primitives';
import { MODULE_KEY } from './modules';

/**
 * The notifications bell: a per-module summary of what is pending/new for the
 * signed-in user, derived live on the server. A subtle dot marks each module
 * with something unseen; clicking an item navigates there and clears its dot.
 */
export function NotificationsBell() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<NotificationsSummaryDto | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    api
      .notificationsSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  // Load on mount and refresh periodically; also refresh when the panel opens.
  useEffect(() => {
    load();
    const id = window.setInterval(load, 60_000);
    return () => window.clearInterval(id);
  }, [load]);
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unseen = summary?.unseenTotal ?? 0;
  const groups = summary?.groups ?? [];

  function moduleLabel(module: PortalModule): string {
    return t(`modules.${MODULE_KEY[module]}`);
  }

  function openItem(module: PortalModule, link: string) {
    setOpen(false);
    // Acknowledge this module, then go. Optimistically clear its dot.
    void api.markNotificationsSeen(module).then(load).catch(load);
    setSummary((s) =>
      s
        ? {
            ...s,
            groups: s.groups.map((g) => (g.module === module ? { ...g, unseen: false } : g)),
          }
        : s,
    );
    navigate(link);
  }

  return (
    <div className="notif" ref={rootRef}>
      <button
        type="button"
        className="erp-icon-btn notif-btn"
        onClick={() => setOpen((v) => !v)}
        title={t('notifications.title')}
        aria-label={t('notifications.title')}
        aria-expanded={open}
      >
        <Icon name="bell" size={19} />
        {unseen > 0 && <span className="notif-badge">{unseen > 9 ? '9+' : unseen}</span>}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label={t('notifications.title')}>
          <div className="notif-head">
            <strong>{t('notifications.title')}</strong>
          </div>
          {groups.length === 0 ? (
            <div className="notif-empty">
              <Icon name="check" size={20} />
              <span>{t('notifications.empty')}</span>
            </div>
          ) : (
            <div className="notif-groups">
              {groups.map((g) => (
                <div className="notif-group" key={g.module}>
                  <div className="notif-group-head">
                    {g.unseen && <span className="notif-dot" aria-hidden="true" />}
                    <span className="notif-group-name">{moduleLabel(g.module)}</span>
                  </div>
                  {g.items.map((it) => (
                    <button
                      type="button"
                      className="notif-item"
                      key={it.kind}
                      onClick={() => openItem(g.module, it.link)}
                    >
                      <span className="notif-item-title">
                        {lang === 'pt' ? it.title : (it.titleEn ?? it.title)}
                      </span>
                      <span className="notif-item-count">{it.count}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
